// "Comments on your content" seen / unseen indicator.
//
// The profile section is server-rendered ERB; this adds a lightweight,
// localStorage-backed "new since you last looked" indicator. We remember the
// highest comment id the owner has seen (per user, per browser) and highlight
// any newer comment, plus show an unseen count badge next to the heading.
//
// Semantics: on first ever visit (nothing stored) every comment counts as
// unseen so the owner discovers the feature. We then persist the newest id so
// subsequent visits only highlight genuinely newer comments.

const STORAGE_PREFIX = 'hiiker:contentCommentsLastSeenId:';

function storageKey(userKey: string): string {
  return `${STORAGE_PREFIX}${userKey || 'self'}`;
}

function readLastSeenId(key: string): number {
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return 0;
  }
}

function writeLastSeenId(key: string, value: number): void {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    /* localStorage unavailable (private mode / disabled) — degrade silently */
  }
}

function initContentCommentsSeen(): void {
  const list = document.getElementById('content-comments-list');
  if (!list) return;

  const cards = Array.from(
    list.querySelectorAll<HTMLElement>('.content-comment-card[data-comment-id]')
  );
  if (cards.length === 0) return;

  const userKey = list.dataset.userKey || 'self';
  const key = storageKey(userKey);
  const lastSeenId = readLastSeenId(key);

  let maxId = 0;
  let unseenCount = 0;

  cards.forEach((card) => {
    const id = parseInt(card.dataset.commentId || '', 10);
    if (!Number.isFinite(id)) return;

    if (id > maxId) maxId = id;

    if (id > lastSeenId) {
      card.classList.add('content-comment-unseen');
      unseenCount += 1;
    }
  });

  const badge = document.getElementById('content-comments-unseen-badge');
  if (badge && unseenCount > 0) {
    badge.textContent = String(unseenCount);
    badge.hidden = false;
    badge.setAttribute(
      'aria-label',
      `${unseenCount} new comment${unseenCount === 1 ? '' : 's'} on your content`
    );
  }

  // Mark everything currently shown as seen for next time.
  if (maxId > lastSeenId) {
    writeLastSeenId(key, maxId);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initContentCommentsSeen);
} else {
  initContentCommentsSeen();
}

export default initContentCommentsSeen;
